// Role-discriminator helpers — single source of truth so BOM,
// validation, exporters, and nesting all read parts the same way.
//
// A `CabinetPart` carries `role` once the DSL evaluator has stamped it.
// Older cabinet rows persisted before the parametric-classes refactor
// may still produce parts without a role; for those we fall back to
// the original id-regex match. This means a freshly evaluated cabinet
// is always covered, and the legacy fallback gives bug-compatible
// behaviour for stale data until it's re-saved.

import type { CabinetPart } from "@/lib/types";

export function isDoorPart(p: CabinetPart): boolean {
  if (p.role) return p.role === "door" || p.role === "door_left" || p.role === "door_right";
  return /^door/.test(p.id);
}

export function isShelfPart(p: CabinetPart): boolean {
  if (p.role) return p.role === "shelf";
  return /^shelf/.test(p.id);
}

export function isSidePart(p: CabinetPart): boolean {
  if (p.role) return p.role === "side_left" || p.role === "side_right";
  return p.id === "left_side" || p.id === "right_side";
}

export function isLeftSidePart(p: CabinetPart): boolean {
  if (p.role) return p.role === "side_left";
  return p.id === "left_side";
}

export function isRightSidePart(p: CabinetPart): boolean {
  if (p.role) return p.role === "side_right";
  return p.id === "right_side";
}

export function isStretcherPart(p: CabinetPart): boolean {
  if (p.role) return p.role === "stretcher_front" || p.role === "stretcher_back";
  return p.id.startsWith("str_");
}

export function isBottomPart(p: CabinetPart): boolean {
  if (p.role) return p.role === "bottom";
  return p.id === "bottom";
}

export function isBackPart(p: CabinetPart): boolean {
  if (p.role) return p.role === "back";
  return p.id === "back";
}

export function isToeKickPart(p: CabinetPart): boolean {
  if (p.role) return p.role === "toe_kick";
  return p.id === "toe_kick";
}

export function isTopPart(p: CabinetPart): boolean {
  return p.role === "top";
}
